import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Alert, Container, Progress, Spinner } from "reactstrap";

import AdatisLogo from "../../../Components/Common/AdatisLogo";
import {
  getPublicLoyaltyProgress,
  PublicLoyaltyProgress,
} from "../../../services/publicLoyaltyService";

const PublicLoyaltyProgressPage: React.FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();

  const [data, setData] = useState<PublicLoyaltyProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  document.title = t("public_loyalty.pageTitle");

  const load = useCallback(async (): Promise<void> => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNotFound(false);
    setExpired(false);

    try {
      const response = await getPublicLoyaltyProgress(token);
      setData(response.data ?? null);
    } catch (err) {
      const apiError = err as { code?: string };

      if (apiError.code === "LOYALTY_TOKEN_EXPIRED") {
        setExpired(true);
        return;
      }

      if (apiError.code === "LOYALTY_TOKEN_INVALID" || apiError.code === "NOT_FOUND") {
        setNotFound(true);
        return;
      }

      setError(t("public_loyalty.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
      <Container style={{ maxWidth: 560 }}>
        <div className="bg-white rounded-3 shadow-lg p-4 p-md-5">
          <div className="text-center mb-4">
            <AdatisLogo height={28} />
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : expired ? (
            <Alert color="warning" className="mb-0">
              {t("public_loyalty.expired")}
            </Alert>
          ) : notFound ? (
            <Alert color="danger" className="mb-0">
              {t("public_loyalty.notFound")}
            </Alert>
          ) : error ? (
            <Alert color="danger" className="mb-0">
              {error}
            </Alert>
          ) : (
            <>
              <p className="text-muted text-uppercase small mb-1">
                {data?.studio_name}
              </p>
              <h1 className="h4 mb-2">{t("public_loyalty.title")}</h1>
              {data?.client_first_name ? (
                <p className="text-muted mb-4">
                  {t("public_loyalty.hello", { name: data.client_first_name })}
                </p>
              ) : (
                <p className="text-muted mb-4">{t("public_loyalty.subtitle")}</p>
              )}

              {!data?.cards?.length ? (
                <Alert color="info" className="mb-0">
                  {t("public_loyalty.empty")}
                </Alert>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {data.cards.map((card, index) => {
                    const required = card.stamps_required ?? 0;
                    const count = card.stamps_count;
                    const pct =
                      required > 0
                        ? Math.min(100, Math.round((count / required) * 100))
                        : 0;
                    const remaining = card.remaining_to_reward ?? 0;

                    return (
                      <div
                        key={`${card.kind}-${card.label}-${index}`}
                        className="border rounded p-3"
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <strong>{card.label}</strong>
                          <span className="text-muted small">
                            {t("public_loyalty.progressLabel", {
                              count,
                              required,
                            })}
                          </span>
                        </div>
                        <Progress value={pct} className="mb-2" style={{ height: 8 }} />
                        <p className="mb-0 small">
                          {card.free_credits > 0
                            ? t("public_loyalty.freeReady", {
                                count: card.free_credits,
                              })
                            : remaining <= 2
                              ? t("public_loyalty.remainingAlmost", {
                                  count: remaining,
                                })
                              : t("public_loyalty.remaining", {
                                  count: remaining,
                                })}
                        </p>
                        {card.reward_validity_days != null ? (
                          <p className="mb-0 mt-1 text-muted small">
                            {t("public_loyalty.validityHint", {
                              days: card.reward_validity_days,
                            })}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default PublicLoyaltyProgressPage;
